import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import CategoryTabs from '../components/CategoryTabs';
import NewsCard from '../components/NewsCard';
import BottomNav from '../components/BottomNav';
import MiniAudioPlayer from '../components/MiniAudioPlayer';
import FullArticleView from '../components/FullArticleView';
import { fetchArticles, fetchCategories, fetchPublishers } from '../services/api';
import { useToast } from '../hooks/use-toast';
import { saveArticleToLibrary, isArticleDownloaded } from '../utils/audioLibrary';
import { useTheme } from '../context/ThemeContext';
import { Share } from '@capacitor/share';
import { App as CapacitorApp } from '@capacitor/app';
import { 
  getCachedCategories, setCachedCategories,
  getCachedPublishers, setCachedPublishers,
  getCachedArticles, setCachedArticles,
  generateFilterKey
} from '../utils/dataCache';
import { trackCategorySelect, trackPublisherSelect, trackAudioPlay } from '../services/analytics';

const HomePage = () => {
  const navigate = useNavigate();
  const { darkMode } = useTheme();
  const [activeCategory, setActiveCategory] = useState('all');
  const [selectedPublisher, setSelectedPublisher] = useState('all');
  const [activeTab, setActiveTab] = useState('home');
  const [audioPlayer, setAudioPlayer] = useState({
    isOpen: false,
    article: null,
    type: null,
    isPlaying: false,
  });
  const [fullArticleView, setFullArticleView] = useState({
    isOpen: false,
    article: null,
  });
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [publishers, setPublishers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingArticles, setDownloadingArticles] = useState(new Set());
  const { toast } = useToast();
  const isInitialMount = useRef(true);

  // Load categories and publishers - try cache first, then update from backend
  useEffect(() => {
    const loadInitialData = async () => {
      // Try to load from cache first for instant display
      const cachedCategories = getCachedCategories();
      const cachedPublishers = getCachedPublishers();
      
      if (cachedCategories && cachedCategories.length > 0) {
        setCategories(cachedCategories);
      }
      if (cachedPublishers && cachedPublishers.length > 0) {
        setPublishers(cachedPublishers);
      }

      // If we have cached data, don't show loading for initial data
      if (cachedCategories && cachedPublishers) {
        // Fetch fresh data in background
        try {
          const [categoriesData, publishersData] = await Promise.all([
            fetchCategories(),
            fetchPublishers()
          ]);
          setCategories(categoriesData);
          setPublishers(publishersData);
          setCachedCategories(categoriesData);
          setCachedPublishers(publishersData);
        } catch (error) {
          console.error('Error refreshing data:', error);
          // Keep using cached data on error
        }
      } else {
        // No cache, fetch from backend
        try {
          const [categoriesData, publishersData] = await Promise.all([
            fetchCategories(),
            fetchPublishers()
          ]);
          setCategories(categoriesData);
          setPublishers(publishersData);
          setCachedCategories(categoriesData);
          setCachedPublishers(publishersData);
        } catch (error) {
          console.error('Error loading initial data:', error);
          toast({
            title: 'Error',
            description: 'Failed to load categories and publishers',
            variant: 'destructive'
          });
        }
      }
    };
    loadInitialData();
  }, [toast]);

  // Handle Android back button - Simplified behavior
  useEffect(() => {
    let backButtonListener;
    
    const setupBackButton = async () => {
      backButtonListener = await CapacitorApp.addListener('backButton', () => {
        // If full article view is open, close it
        if (fullArticleView.isOpen) {
          setFullArticleView({ isOpen: false, article: null });
          return;
        }
        
        // If audio player is open, stop and close it
        if (audioPlayer.isOpen) {
          setAudioPlayer({ isOpen: false, article: null, type: null, isPlaying: false });
          return;
        }
        
        // Otherwise exit app
        CapacitorApp.exitApp();
      });
    };
    
    setupBackButton();

    return () => {
      if (backButtonListener) {
        backButtonListener.remove();
      }
    };
  }, [fullArticleView.isOpen, audioPlayer.isOpen]);


  // Fetch articles based on filters - use cache for instant display, always refresh in background
  useEffect(() => {
    const loadArticles = async () => {
      // Find the category name from the ID
      let categoryFilter = null;
      if (activeCategory && activeCategory !== 'all') {
        const category = categories.find(c => c.id === activeCategory);
        categoryFilter = category ? category.name : null;
      }
      
      // Find the publisher name from the ID
      let publisherFilter = null;
      if (selectedPublisher && selectedPublisher !== 'all') {
        const publisher = publishers.find(p => p.id === selectedPublisher);
        publisherFilter = publisher ? publisher.name : null;
      }

      const filterKey = generateFilterKey(categoryFilter, publisherFilter);
      
      // Try to load from cache first for instant display
      const cachedArticles = getCachedArticles(filterKey);
      
      if (cachedArticles && cachedArticles.length > 0) {
        setArticles(cachedArticles);
        setLoading(false);
      } else {
        setLoading(true);
      }
      
      // Always fetch fresh data from backend
      try {
        const data = await fetchArticles(categoryFilter, publisherFilter);
        setArticles(data.articles);
        setCachedArticles(filterKey, data.articles);
      } catch (error) {
        console.error('Error loading articles:', error);
        // Only show error if we don't have cached data
        if (!cachedArticles || cachedArticles.length === 0) {
          toast({
            title: 'Error',
            description: 'Failed to load articles',
            variant: 'destructive'
          });
        }
      } finally {
        setLoading(false);
      }
    };
    
    // Only load articles if categories and publishers are loaded
    if (categories.length > 0 && publishers.length > 0) {
      loadArticles();
    }
  }, [activeCategory, selectedPublisher, categories, publishers, toast]);

  const handlePlaySummary = useCallback((article) => {
    // Track audio play
    trackAudioPlay(article.id, article.title);
    
    // If clicking the same article's summary that's already playing, pause it
    if (audioPlayer.article?.id === article.id && audioPlayer.type === 'summary' && audioPlayer.isPlaying) {
      setAudioPlayer(prev => ({ ...prev, isPlaying: false }));
    } else {
      // Otherwise, play the new article
      setAudioPlayer({
        isOpen: true,
        article,
        type: 'summary',
        isPlaying: true,
      });
    }
  }, [audioPlayer.article?.id, audioPlayer.type, audioPlayer.isPlaying]);

  const handlePlayFull = useCallback((article) => {
    // Track audio play
    trackAudioPlay(article.id, article.title);
    
    // If clicking the same article's full that's already playing, pause it
    if (audioPlayer.article?.id === article.id && audioPlayer.type === 'full' && audioPlayer.isPlaying) {
      setAudioPlayer(prev => ({ ...prev, isPlaying: false }));
    } else {
      // Otherwise, play the new article
      setAudioPlayer({
        isOpen: true,
        article,
        type: 'full',
        isPlaying: true,
      });
    }
  }, [audioPlayer.article?.id, audioPlayer.type, audioPlayer.isPlaying]);

  const handleTextRead = useCallback((article) => {
    setFullArticleView({
      isOpen: true,
      article,
    });
  }, []);

  const handleDownload = useCallback((article) => {
    // Check if already downloaded
    if (isArticleDownloaded(article.id)) {
      toast({
        title: 'Already Downloaded',
        description: 'This article is already in your library',
      });
      return;
    }

    // Check if currently downloading
    if (downloadingArticles.has(article.id)) {
      return;
    }

    // Add to downloading set
    setDownloadingArticles(prev => new Set(prev).add(article.id));

    // Simulate download with progress (like YouTube)
    setTimeout(() => {
      const result = saveArticleToLibrary(article);
      
      // Remove from downloading set
      setDownloadingArticles(prev => {
        const newSet = new Set(prev);
        newSet.delete(article.id);
        return newSet;
      });

      if (result.success) {
        toast({
          title: '✓ Downloaded',
          description: `"${article.title}" saved to Audio Library`,
        });
      } else {
        toast({
          title: 'Error',
          description: result.message,
          variant: 'destructive'
        });
      }
    }, 2000); // 2 second download animation
  }, [toast, downloadingArticles]);

  const handleShare = useCallback(async (article) => {
    try {
      const shareCaption = 'Listen to the news, Save your time - Download SmartReadGo';
      const shareUrl = 'https://smartreadgo.com';
      
      // Share with both title and full message in text
      const shareMessage = `${article.title}\n\n${shareCaption}\n\n${shareUrl}`;
      
      // Try native share
      if (navigator.share) {
        await navigator.share({
          title: article.title,
          text: shareMessage
        });
      } else {
        // Fallback: Capacitor share
        await Share.share({
          title: article.title,
          text: shareMessage,
          dialogTitle: 'Share Article'
        });
      }
    } catch (error) {
      if (!error.message?.includes('cancel') && !error.message?.includes('abort')) {
        console.error('Share error:', error);
        // Copy to clipboard as fallback
        const shareCaption = 'Listen to the news, Save your time - Download SmartReadGo';
        const shareUrl = 'https://smartreadgo.com';
        const shareMessage = `${article.title}\n\n${shareCaption}\n\n${shareUrl}`;
        
        await navigator.clipboard.writeText(shareMessage);
        toast({
          title: 'Copied to Clipboard',
          description: 'Share message copied!',
        });
      }
    }
  }, [toast]);

  const handleTabChange = useCallback((tab) => {
    setActiveTab(tab);
    if (tab === 'drive') {
      navigate('/drive-mode');
    } else if (tab !== 'home') {
      toast({
        title: `${tab.charAt(0).toUpperCase() + tab.slice(1)} Mode`,
        description: 'This feature will be implemented in the next phase.',
      });
    }
  }, [navigate, toast]);

  const handleCloseAudioPlayer = useCallback(() => {
    setAudioPlayer({ isOpen: false, article: null, type: null, isPlaying: false });
  }, []);

  const handleAudioPlayerStateChange = useCallback((isPlaying) => {
    setAudioPlayer(prev => ({ ...prev, isPlaying }));
  }, []);

  const handleCloseFullArticle = useCallback(() => {
    setFullArticleView({ isOpen: false, article: null });
  }, []);

  // Handlers with analytics tracking
  const handleCategoryChange = useCallback((categoryId) => {
    setActiveCategory(categoryId);
    const category = categories.find(c => c.id === categoryId);
    if (category) {
      trackCategorySelect(category.name);
    }
  }, [categories]);

  const handlePublisherChange = useCallback((publisherId) => {
    setSelectedPublisher(publisherId);
    const publisher = publishers.find(p => p.id === publisherId);
    if (publisher) {
      trackPublisherSelect(publisher.name);
    }
  }, [publishers]);

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      <Header
        selectedPublisher={selectedPublisher}
        onPublisherChange={handlePublisherChange}
        publishers={publishers}
        darkMode={darkMode}
      />

      <CategoryTabs
        categories={categories}
        activeCategory={activeCategory}
        onCategoryChange={handleCategoryChange}
        darkMode={darkMode}
      />

      <main className={`px-4 py-4 ${audioPlayer.isOpen ? 'pb-32' : 'pb-20'}`}>
        {loading ? (
          <div className="text-center py-12">
            <p className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Loading articles...</p>
          </div>
        ) : articles.length > 0 ? (
          articles.map((article) => (
            <div key={article.id} id={`news-card-${article.id}`}>
              <NewsCard
                article={article}
                onPlaySummary={handlePlaySummary}
                onPlayFull={handlePlayFull}
                onTextRead={handleTextRead}
                onDownload={handleDownload}
                onShare={handleShare}
                isPlayingSummary={audioPlayer.article?.id === article.id && audioPlayer.type === 'summary' && audioPlayer.isPlaying}
                isPlayingFull={audioPlayer.article?.id === article.id && audioPlayer.type === 'full' && audioPlayer.isPlaying}
                isDownloaded={isArticleDownloaded(article.id)}
                isDownloading={downloadingArticles.has(article.id)}
                darkMode={darkMode}
              />
            </div>
          ))
        ) : (
          <div className="text-center py-12">
            <p className={darkMode ? 'text-gray-400' : 'text-gray-500'}>No articles found for the selected filters.</p>
          </div>
        )}
      </main>

      <BottomNav activeTab={activeTab} onTabChange={handleTabChange} darkMode={darkMode} />

      {audioPlayer.isOpen && (
        <MiniAudioPlayer
          article={audioPlayer.article}
          audioType={audioPlayer.type}
          onClose={handleCloseAudioPlayer}
          initialIsPlaying={audioPlayer.isPlaying}
          onPlayingStateChange={handleAudioPlayerStateChange}
          darkMode={darkMode}
        />
      )}

      {fullArticleView.isOpen && (
        <FullArticleView
          article={fullArticleView.article}
          onClose={handleCloseFullArticle}
          darkMode={darkMode}
        />
      )}
    </div>
  );
};

export default HomePage;